import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  IconClose,
  InformationalScreen,
  LoadingModal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';

import {
  Button,
  Footer,
  MainContainer,
  OptInOut,
  RichText,
} from '~/components/utility';
import { ApiStatus, APP_RETURN_TIMEOUT, ErrorCode } from '~/constants';
import { useApiError } from '~/hooks';
import { type RootState } from '~/reducers';
import { AUTH_SESSION } from '~/reducers/auth/types';
import { handleConsent } from '~/services/consent';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';
import { logout } from '~/utilities/session';

const mapStateToProps = ({
  env: { osType, osVersion, appVersion, deviceId },
  auth,
  nav: { query },
}: RootState) => ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  auth,
  isAccountProfile: !!query.accountProfile,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const testID = 'rwe-consent';

const RWEConsent = ({
  osType,
  osVersion,
  isAccountProfile,
  appVersion,
  auth,
  deviceId,
  dispatch,
}: Props) => {
  const [loading, setLoading] = useState(false);

  const { showApiErrorModal, showNetworkErrorModal } = useApiError();

  const handleClickLink = (href: string) => {
    mediator.publish('router:navigate', href);
  };

  const handleApiRequestError = useCallback(
    (error: AxiosError<ApiErrorData>) => {
      if (error.status === ApiStatus.UNAUTHORIZED) {
        logout();
      } else if (error.code === ErrorCode.NETWORK_ERROR) {
        showNetworkErrorModal();
      } else {
        showApiErrorModal();
      }
    },
    [showApiErrorModal, showNetworkErrorModal],
  );

  const handleConsentWrapper = useCallback(
    async (action: string) => {
      try {
        const resp = await handleConsent({
          dataId: 'realWorldEvidence',
          patientId: auth.account_id,
          deviceId,
          action,
          osType,
          osVersion,
          appVersion,
        });

        const updatedAuthSession: AuthSession = {
          ...auth,
          include: {
            patient: {
              ...(auth.include?.patient ?? {}),
              consents: {
                ...auth.include?.patient?.consents,
                realWorldEvidence: { ...resp },
              },
            },
          },
        };

        dispatch({ type: AUTH_SESSION, authSession: updatedAuthSession });

        notifyApp('adc-webview:consents', {
          id: 'realWorldEvidence',
          action,
          location: isAccountProfile ? 'profile' : 'registration',
        });

        return updatedAuthSession;
      } catch (error) {
        handleApiRequestError(error as AxiosError<ApiErrorData>);

        return null;
      }
    },
    [
      appVersion,
      auth,
      handleApiRequestError,
      isAccountProfile,
      osType,
      osVersion,
      deviceId,
      dispatch,
    ],
  );

  const handleSubmitRWEAction = useCallback(
    async (action: string) => {
      try {
        setLoading(true);

        const updatedAuthSession = await handleConsentWrapper(action);

        notifyApp<AuthSession | null>('adc-webview:signin', updatedAuthSession);

        // This timeout is added to give the app time to redirect the user back.
        setTimeout(() => {
          setLoading(false);
        }, APP_RETURN_TIMEOUT);
      } catch {
        setLoading(false);
      }
    },
    [handleConsentWrapper],
  );

  const onClosePressed = useCallback(() => {
    mediator.publish('router:back');
  }, []);

  return (
    <MainContainer testID={testID}>
      {loading && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}
      <InformationalScreen
        testID={buildTestId({ testID, suffix: 'screen' })}
        topNavigationProps={
          isAccountProfile
            ? {
                animatedTitle: true,
                title: i18n.t('Research.title'),
                buttonStart: {
                  element: (
                    <IconClose
                      testID={buildTestId({
                        testID,
                        suffix: ['close', 'button'],
                      })}
                    />
                  ),
                  label: i18n.t('Research.close'),
                  onPress: onClosePressed,
                },
                testID: buildTestId({ testID, suffix: 'nav' }),
              }
            : {
                title: undefined,
                testID: buildTestId({ testID, suffix: 'nav' }),
              }
        }
      >
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
        >
          <InformationalScreen.Content>
            {!isAccountProfile ? (
              <Text
                type="$h1.strong"
                testID={buildTestId({ testID, suffix: 'title' })}
              >
                {i18n.t('Research.title')}
              </Text>
            ) : undefined}
            <RichText
              testID={buildTestId({
                testID,
                suffix: ['body', 'text'],
              })}
              onPressLink={handleClickLink}
              text={i18n.t('Research.content.researchRichText.bodyText')}
            />
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            {!isAccountProfile ? (
              <Footer>
                <Stack alignSelf="flex-end" width="100%" gap="$4">
                  <Button
                    onPress={() => void handleSubmitRWEAction('accept')}
                    testID={buildTestId({
                      testID,
                      suffix: ['opt', 'in', 'button'],
                    })}
                    label={i18n.t('Global.microcopy.common.accept')}
                    aria-label={i18n.t('Global.microcopy.common.accept')}
                  />
                  <Button
                    variant="tertiary"
                    onPress={() => void handleSubmitRWEAction('decline')}
                    label={i18n.t('Global.microcopy.common.decline')}
                    aria-label={i18n.t('Global.microcopy.common.decline')}
                    testID={buildTestId({
                      testID,
                      suffix: ['opt', 'out', 'button'],
                    })}
                  />
                </Stack>
              </Footer>
            ) : (
              <OptInOut
                testID={buildTestId({ testID, suffix: ['opt', 'in', 'out'] })}
                optionStrings={{
                  titleTag: 'Global.microcopy.common.status',
                  positiveTag: 'Global.microcopy.common.optedIn',
                  negativeTag: 'Global.microcopy.common.declined',
                  positiveOption: 'Global.microcopy.common.optIn',
                  negativeOption: 'Global.microcopy.common.decline',
                }}
                initialState={
                  auth.include?.patient?.consents?.realWorldEvidence?.accepted
                    ? 'accept'
                    : 'decline'
                }
                handleConsent={handleConsentWrapper}
              />
            )}
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(RWEConsent);
